import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import { GetIFRSQuestionResponseOptions } from "../../api/Quiz/GetIFRSQuestionResponseOptions";
import { GetIFRSQuestionById } from "../../api/Quiz/GetIFRSQuestionById";
import Button from "../../components/Shared/Button";
import { FileText, FileSpreadsheet, Eye } from "lucide-react";
import TrashIcon from "../../components/Icons/TrashIcon";
import DeleteModal from "../../components/Shared/DeleteModal";
import { DeleteIFRSQuestionResponseOption } from "../../api/Quiz/DeleteIFRSQuestionResponseOption";
import { toast } from "react-toastify";
import AddIcon from "../../components/Icons/AddIcon";
import FilePreviewModal from "../../components/Dashboard/ResponseOptions/FilePreviewModal";
import { useUserDataStore } from "../../stores/user";

const IFRSQuestionResponseOptions = () => {
  const { courseId, exam_id, question_id } = useParams<{
    courseId: string;
    exam_id: string;
    question_id: string;
  }>();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<{
    url: string;
    type: string;
  } | null>(null);
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  // Fetch question details for title
  const { isLoading: isQuestionLoading } = useQuery({
    queryFn: () => GetIFRSQuestionById(exam_id || "", question_id || ""),
    queryKey: ["ifrs-question", exam_id, question_id],
    enabled: !!exam_id && !!question_id && userRole !== "admin",
    refetchOnWindowFocus: false,
  });

  // Fetch response options with pagination
  const { data: ResponseOptionsResponse, isLoading: isResponseOptionsLoading } =
    useQuery({
      queryFn: () =>
        GetIFRSQuestionResponseOptions(question_id || "", {
          page,
          limit,
          sort: "asc",
        }),
      queryKey: ["ifrs-question-response-options", question_id, page, limit],
      enabled: !!question_id,
      staleTime: 0,
      refetchInterval: 0,
      refetchOnWindowFocus: false,
    });

  const responseOptions = ResponseOptionsResponse?.data?.data || [];
  const meta = ResponseOptionsResponse?.data?.meta;

  const getFileIcon = (fileType: string) => {
    if (fileType === "spreadsheet") {
      return <FileSpreadsheet size={20} className="text-green-500" />;
    }
    return <FileText size={20} className="text-blue-500" />;
  };

  const getFileUrl = (url: string) => {
    if (url.startsWith("http")) {
      return url;
    }
    // Construct S3 URL from key
    return `https://nilewisect.s3.amazonaws.com/${url}`;
  };

  if (isQuestionLoading || isResponseOptionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions`}
          className="font-semibold text-primary hover:text-primary/80"
        >
          ← Back to Questions
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          Question Response Options
        </h1>
      </div>

      <div className="p-8 bg-white shadow rounded-xl border-black/15">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Response Options List</h2>
          <Link to={`create`}>
            <Button
              className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
              variant="primary"
            >
              <AddIcon className="mr-1.5" />
              Create Response Option
            </Button>
          </Link>
        </div>

        {responseOptions.length > 0 ? (
          <>
            <div className="overflow-x-auto scrolling-touch scrollbar-thumb-blue scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-w-2">
              <table className="w-full table-auto">
                <thead className="text-lg capitalize">
                  <tr className="bg-[#F1F4F9] rounded-t-lg">
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">ID</div>
                    </th>
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">File Type</div>
                    </th>

                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">Answer File</div>
                    </th>
                    <th className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                      <div className="text-sm text-left">Actions</div>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {responseOptions.map((option) => (
                    <tr key={option.id}>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        <div className="inline-flex rounded-full text-left py-0.5 text-sm font-semibold">
                          {option.id}
                        </div>
                      </td>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        <a
                          href={getFileUrl(option.answerFileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm cursor-pointer text-primary hover:text-primary/80"
                        >
                          {getFileIcon(option.fileType)}
                          <span className="capitalize">{option.fileType}</span>
                        </a>
                      </td>

                      <td className="px-2 py-3 first:pl-5 last:pr-5">
                        <a
                          href={getFileUrl(option.answerFileUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block max-w-md text-sm truncate text-primary hover:text-primary/80"
                        >
                          {option.answerFileUrl}
                        </a>
                      </td>
                      <td className="px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                        <div className="flex items-stretch justify-center gap-3">
                          <button
                            onClick={() => {
                              setSelectedFile({
                                url: getFileUrl(option.answerFileUrl),
                                type: option.fileType,
                              });
                              setIsPreviewOpen(true);
                            }}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold transition-colors border text-primary hover:bg-gray-50 rounded-xl"
                          >
                            <Eye size={16} />
                            Preview
                          </button>
                          <ResponseOptionDeleteButton
                            questionId={question_id || ""}
                            responseOptionId={option.id}
                            onDelete={() => {
                              queryClient.invalidateQueries({
                                queryKey: [
                                  "ifrs-question-response-options",
                                  question_id,
                                ],
                              });
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {meta && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Showing {responseOptions.length} of {meta.total} response
                  options
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="gray"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={!meta.hasPreviousPage || isResponseOptionsLoading}
                    className="!h-10 !text-sm"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center px-4 text-sm font-semibold">
                    Page {meta.page} of {meta.lastPage}
                  </div>
                  <Button
                    variant="gray"
                    onClick={() => setPage((prev) => prev + 1)}
                    disabled={!meta.hasNextPage || isResponseOptionsLoading}
                    className="!h-10 !text-sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              No response options found for this question
            </p>
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fileUrl={selectedFile?.url || ""}
        fileType={selectedFile?.type || ""}
      />
    </div>
  );
};

interface ResponseOptionDeleteButtonProps {
  questionId: string;
  responseOptionId: string;
  onDelete?: () => void;
}

const ResponseOptionDeleteButton = ({
  questionId,
  responseOptionId,
  onDelete,
}: ResponseOptionDeleteButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleDelete = async (id: string) => {
    const result = await DeleteIFRSQuestionResponseOption(questionId, id);
    if (!result.error) {
      if (onDelete) {
        onDelete();
      }
      queryClient.invalidateQueries({
        queryKey: ["ifrs-question-response-options", questionId],
      });
      toast.success("Response option deleted successfully");
    } else {
      toast.error("Failed to delete response option");
    }
  };

  return (
    <>
      <div className="flex items-center justify-center flex-shrink-0 p-3 text-sm font-semibold border cursor-pointer rounded-xl hover:bg-gray-50">
        <button onClick={() => setIsOpen(true)}>
          <TrashIcon />
        </button>
      </div>
      <DeleteModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={responseOptionId}
        queryKey={`ifrs-question-response-options-${questionId}`}
      />
    </>
  );
};

export default IFRSQuestionResponseOptions;
